"use client";

import { useEffect, useRef } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { ReactNode } from "react";

interface Props {
  children: ReactNode;
  className?: string;
  delay?: number;
  id?: string;
  style?: React.CSSProperties;
}

const VISIBLE = { opacity: 1, y: 0 };
const HIDDEN_SECTION = { opacity: 0, y: 24 };
const HIDDEN_ELEMENT = { opacity: 0, y: 16 };

// Shared hook: inView + fallback timer guarantee visibility
function useGuaranteedAnimation() {
  const ref = useRef<HTMLElement>(null);
  const controls = useAnimation();

  const inView = useInView(ref as React.RefObject<Element>, {
    once: true,
    // positive bottom margin = fires 200px BEFORE element enters viewport
    margin: "0px 0px 200px 0px",
    amount: 0.01,
  });

  // Primary: fire when observer triggers
  useEffect(() => {
    if (inView) controls.start(VISIBLE);
  }, [inView, controls]);

  // Fallback: if observer never fires for any reason, force visible at 1.5s
  useEffect(() => {
    const t = setTimeout(() => controls.start(VISIBLE), 1500);
    return () => clearTimeout(t);
  }, [controls]);

  return { ref, controls };
}

export default function AnimatedSection({
  children,
  className = "",
  delay = 0,
  id,
}: Props) {
  const { ref, controls } = useGuaranteedAnimation();

  return (
    <motion.section
      ref={ref as React.RefObject<HTMLElement>}
      id={id}
      initial={HIDDEN_SECTION}
      animate={controls}
      transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98], delay }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

export function AnimatedElement({
  children,
  className = "",
  delay = 0,
  as = "div",
  style,
}: Props & { as?: any }) {
  const { ref, controls } = useGuaranteedAnimation();
  const Component = motion[as as keyof typeof motion] as any;

  return (
    <Component
      ref={ref}
      initial={HIDDEN_ELEMENT}
      animate={controls}
      transition={{ duration: 0.6, ease: [0.21, 0.47, 0.32, 0.98], delay }}
      className={className}
      style={style}
    >
      {children}
    </Component>
  );
}
